/**
 * anonTokenVault.ts
 * Encrypted (AES-GCM) IndexedDB vault for anonymous edit tokens.
 * Falls back to localStorage ONLY if IDB/WebCrypto are unavailable or fail.
 * 
 * Tokens are stored with millisecond timestamps to prevent stale imports from
 * overwriting fresher tokens after rotation.
 */

export type AnonEntity = 'impact' | 'teabate' | 'comment' | 'solution' | 'feedback' | 'action' | 'collection';

/**
 * Token payload stored in vault (encrypted)
 */
type TokenPayload = {
  token: string;
  ts: number; // Timestamp in milliseconds
};

const DB_NAME = 'societea_anon_vault';
const DB_VERSION = 1;
const TOKENS = 'tokens';
const KEYS = 'keys';
const AES_KEY_ID = 'anon_aes_gcm_key_v1';

// ---------- text/binary helpers ----------
const enc = (s: string) => new TextEncoder().encode(s);
const dec = (b: ArrayBuffer) => new TextDecoder().decode(b);
const b64 = (a: ArrayBuffer): string => {
    const bytes = new Uint8Array(a);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };
const ub64 = (s: string) => Uint8Array.from(atob(s), c => c.charCodeAt(0)).buffer;

// ---------- environment gates ----------
function canUseIDB() { return typeof indexedDB !== 'undefined'; }
function canUseCrypto() {
  return typeof crypto !== 'undefined' && !!crypto.subtle &&
         (isSecureContext || window.location.hostname === 'localhost');
}

// ---------- single cached connection ----------
let _dbPromise: Promise<IDBDatabase> | null = null;
function getDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TOKENS)) db.createObjectStore(TOKENS);
      if (!db.objectStoreNames.contains(KEYS)) db.createObjectStore(KEYS);
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        try { db.close(); } catch {}
        _dbPromise = null; // force reopen next time
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IDB open blocked'));
  });
  return _dbPromise;
}

// ---------- immediate transaction helpers ----------
function idbPut(storeName: string, key: IDBValidKey, value: any): Promise<void> {
  return getDB().then(db => new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put(value, key); // queue synchronously
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error || new Error('IDB tx abort'));
      tx.onerror = () => reject(tx.error || new Error('IDB tx error'));
    } catch (e) { reject(e); }
  }));
}

function idbGet<T = any>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  return getDB().then(db => new Promise<T | undefined>((resolve, reject) => {
    try {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
      tx.onabort = () => reject(tx.error || new Error('IDB tx abort'));
    } catch (e) { reject(e); }
  }));
}

// ---------- key management (crypto ops OUTSIDE transaction scope) ----------
async function getOrCreateKey(): Promise<CryptoKey> {
  // First, try to get existing key (read-only transaction)
  const raw = await idbGet<ArrayBuffer>(KEYS, AES_KEY_ID);
  
  if (raw) {
    // Key exists - import and return it
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }
  
  // Key doesn't exist - generate it (ALL crypto ops BEFORE transaction)
  const k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const exported = await crypto.subtle.exportKey('raw', k);
  
  // Now write it (single synchronous put operation)
  await idbPut(KEYS, AES_KEY_ID, exported);
  
  // Import and return the key
  return crypto.subtle.importKey('raw', exported, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

// ---------- public API ----------
export function entityKey(t: AnonEntity, id: number | string) {
  return `${t}:${id}`;
}

/**
 * Save an anonymous token with timestamp
 * Only saves if the new timestamp is newer than existing (prevents stale overwrites)
 */
export async function saveAnonToken(
  t: AnonEntity,
  id: number | string,
  token: string,
  timestamp?: number
): Promise<void> {
  const ts = timestamp || Date.now();
  const lsKey = `societea:anon:${entityKey(t, id)}`;
  
  try {
    if (!canUseIDB()) throw new Error('IDB unavailable');
    if (!canUseCrypto()) throw new Error('WebCrypto unavailable (non-secure context?)');

    // Check if we have an existing token with timestamp
    const existing = await loadAnonTokenWithTimestamp(t, id);
    if (existing && existing.ts >= ts) {
      // Existing token is newer or same age - don't overwrite
      console.warn(`[anonTokenVault] Skipping save for ${t}:${id} - existing token is newer`);
      return;
    }

    // Create payload with timestamp
    const payloadObj: TokenPayload = { token, ts };
    const payloadJson = JSON.stringify(payloadObj);

    // crypto first, then one immediate IDB op
    const key = await getOrCreateKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc(payloadJson));
    const encryptedPayload = JSON.stringify({ iv: b64(iv.buffer), data: b64(ct) });

    await idbPut(TOKENS, entityKey(t, id), encryptedPayload);
  } catch (e: any) {
    console.warn(`[anonTokenVault] Falling back to localStorage for ${t}:${id}:`, e?.name || e, e?.message || '');
    try {
      // Store with timestamp in localStorage too
      const lsPayload = JSON.stringify({ token, ts });
      localStorage.setItem(lsKey, lsPayload);
    } catch {}
  }
}

/**
 * Load an anonymous token (without timestamp metadata)
 */
export async function loadAnonToken(t: AnonEntity, id: number | string): Promise<string | null> {
  const result = await loadAnonTokenWithTimestamp(t, id);
  return result?.token || null;
}

/**
 * Load an anonymous token with its timestamp
 * Used internally for timestamp comparisons during save
 */
async function loadAnonTokenWithTimestamp(
  t: AnonEntity,
  id: number | string
): Promise<TokenPayload | null> {
  const lsKey = `societea:anon:${entityKey(t, id)}`;
  
  try {
    if (!canUseIDB() || !canUseCrypto()) throw new Error('NoIDBOrCrypto');

    const raw = await idbGet<string>(TOKENS, entityKey(t, id));
    if (!raw) {
      // Try localStorage fallback
      const lsData = localStorage.getItem(lsKey);
      if (!lsData) return null;
      
      try {
        const parsed = JSON.parse(lsData) as TokenPayload;
        return parsed;
      } catch {
        // Legacy format (plain string) - add timestamp as 0
        return { token: lsData, ts: 0 };
      }
    }

    // Decrypt from IndexedDB
    const { iv, data } = JSON.parse(raw) as { iv: string; data: string };
    const key = await getOrCreateKey();
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ub64(iv) }, key, ub64(data));
    const decrypted = dec(pt);
    
    try {
      const parsed = JSON.parse(decrypted) as TokenPayload;
      return parsed;
    } catch {
      // Legacy format (plain string) - add timestamp as 0
      return { token: decrypted, ts: 0 };
    }
  } catch {
    // Fallback to localStorage
    const lsData = localStorage.getItem(lsKey);
    if (!lsData) return null;
    
    try {
      const parsed = JSON.parse(lsData) as TokenPayload;
      return parsed;
    } catch {
      // Legacy format
      return { token: lsData, ts: 0 };
    }
  }
}
